import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';

import AgenticCommerceOnItsWayIllustration, {
  ON_ITS_WAY_ILLUSTRATION_WIDTH,
} from './agentic_commerce_on_its_way_illustration';
import AgenticCommerceUpsellIllustration, { ILLUSTRATION_WIDTH } from './agentic_commerce_upsell_illustration';

export const EmptyStateVariant = Object.freeze({
  ON_ITS_WAY: 'ON_ITS_WAY',
  UPSELL: 'UPSELL',
});

export default function AgenticCommerceEmptyState({ isSidebarCollapsed, variant }) {
  const isUpsell = variant === EmptyStateVariant.UPSELL;
  const illustrationWidth = getIllustrationWidth({ isSidebarCollapsed, isUpsell });

  return (
    <EmptyStateContainer
      $isSidebarCollapsed={isSidebarCollapsed}
      data-aid={isUpsell ? 'agenticCommerce-upsell' : 'agenticCommerce-onItsWay'}
    >
      <TextColumn>{isUpsell ? <UpsellText /> : <OnItsWayText />}</TextColumn>
      <IllustrationFigure $width={illustrationWidth}>
        {isUpsell ? (
          <AgenticCommerceUpsellIllustration scale={illustrationWidth / ILLUSTRATION_WIDTH} />
        ) : (
          <AgenticCommerceOnItsWayIllustration scale={illustrationWidth / ON_ITS_WAY_ILLUSTRATION_WIDTH} />
        )}
      </IllustrationFigure>
    </EmptyStateContainer>
  );
}

AgenticCommerceEmptyState.propTypes = {
  isSidebarCollapsed: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(Object.values(EmptyStateVariant)).isRequired,
};

function OnItsWayText() {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <Heading>{t('Your Agentic Commerce dashboard is on its way')}</Heading>
      <Body>
        {t(
          "We're setting up reporting for your account. Soon you'll see the revenue your conversations drive, how customers go from Chat to checkout, and how each week compares."
        )}
      </Body>
    </React.Fragment>
  );
}

function UpsellText() {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <Heading>
        <Trans components={{ br: <br /> }} i18nKey="Turn shopping conversations<br />into revenue" />
      </Heading>
      <Body>
        {t(
          'Let Gladly AI help shoppers find products, add to cart, and check out, right in Chat. Talk to your Gladly representative to get set up and see results like these.'
        )}
      </Body>
    </React.Fragment>
  );
}

// The upsell illustration is taller than it is wide, so it stays at its design size instead of growing to 710px.
function getIllustrationWidth({ isSidebarCollapsed, isUpsell }) {
  if (!isSidebarCollapsed) {
    return 512;
  }
  return isUpsell ? ILLUSTRATION_WIDTH : 710;
}

const EmptyStateContainer = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 48px 72px;
  justify-content: ${p => (p.$isSidebarCollapsed ? 'center' : 'flex-start')};
  min-height: 100%;
  padding: 48px;
`;

const TextColumn = styled.div`
  display: flex;
  flex: 0 1 420px;
  flex-direction: column;
  gap: 16px;
`;

const Heading = styled.h1`
  color: ${p => p.theme.colors.black};
  font-size: 24px;
  font-weight: ${p => p.theme.fontWeight.heavy};
  line-height: 30px;
  margin: 0;
`;

const Body = styled.p`
  color: ${p => p.theme.colors.gray800};
  font-size: 16px;
  line-height: 24px;
  margin: 0;
`;

const IllustrationFigure = styled.figure`
  cursor: default;
  flex: 0 0 ${p => p.$width}px;
  margin: 0;
  max-width: 100%;
  min-width: 0;
`;
